/**
 * URL helpers for the player-side QR code shown on the host TV.
 * Rendering is left to the caller (typically `<QRCodeSVG>` from `qrcode.react`).
 */

import type { GameId } from '@mdev/common'

/**
 * Build the absolute URL a player should scan/click to join a game on their
 * phone. `origin` is normally `window.location.origin`; passing it in makes
 * this function safe to call from server-side / test code as well.
 *
 *   buildPlayUrl( 'https://games.example.com', 'scotland-yard', 'AB23', 'uuid' )
 *   -> 'https://games.example.com/games/scotland-yard/play/AB23/uuid'
 *
 * The `/games` prefix is required — the games router is mounted at `/games/*`
 * (see `App.tsx`), so a link without it 404s.
 */
export function buildPlayUrl(
  origin: string,
  gameId: GameId,
  code: string,
  uid: string,
): string {
  const trimmed = origin.replace( /\/+$/, '' )
  return `${ trimmed }/games/${ gameId }/play/${ code }/${ uid }`
}

/** Companion helper for the TV-facing host URL — same shape, different verb. */
export function buildHostUrl( origin: string, gameId: GameId, code: string ): string {
  const trimmed = origin.replace( /\/+$/, '' )
  return `${ trimmed }/games/${ gameId }/host/${ code }`
}
